import {
  CompanyRole,
  VendorProfile,
  VendorProjectFragment,
} from "@/__generated__/graphql";
import SearchIcon from "@/assets/Search";
import { InfiniteScroll } from "@/components/common/InfiniteScroll";

import { useProjectSearch } from "@/components/ProjectSelector/hooks";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useUserContext } from "@/contexts/UserContextProvider";
import { ProjectIcon } from "@/helpers/icons";
import { PROJECT_INVOICES_URL } from "@/helpers/url";
import { longTextFormatter } from "@/helpers/utils";
import { getProjectIconColor } from "@/theme/colors";
import { ChevronDown, FolderOpenDot, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";

function DropdownContent() {
  const { setCurrentProject, role } = useUserContext();
  const { hasNextPage, loading, loadMore, projects, searchProject } =
    useProjectSearch();
  const navigate = useNavigate();

  return (
    <>
      <div className="flex items-center border-[1px] border-accent-foreground rounded-sm gap-2">
        <SearchIcon
          className="ml-2 w-5 h-4"
          color="var(--color-secondary-foreground)"
        />

        <Input
          type="text"
          placeholder="Search Project"
          aria-label="Search Project"
          className="border-none bg-transparent! placeholder:text-sm p-0"
          autoFocus
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
          onChange={(e) => searchProject(e.target.value)}
        />
      </div>

      <DropdownMenuSeparator className="my-2 text-secondary-foreground" />

      <DropdownMenuGroup className="max-h-[250px] overflow-scroll">
        {loading ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Skeleton className="h-7 w-[30%]" />
              <Skeleton className="h-7 w-[20%]" />
            </div>
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="h-8 w-full" />
            ))}
          </div>
        ) : projects.length ? (
          <InfiniteScroll
            loadMore={loadMore}
            hasMore={hasNextPage}
            loader={<Skeleton className="h-8 w-full" />}
          >
            <div className="flex justify-between pt-2 items-end">
              <div className="text-base font-semibold ">Recent Projects</div>

              <div
                className="cursor-pointer font-medium text-sm mr-2"
                onClick={() => setCurrentProject(null)}
              >
                View All
              </div>
            </div>

            <div className="py-1">
              {projects.map((project) => (
                <DropdownMenuItem
                  key={project.id}
                  className="cursor-pointer py-2"
                  onClick={() => {
                    setCurrentProject(project.id);
                    const isOnInvoicePage =
                      location.pathname.includes("/invoices");

                    if (isOnInvoicePage) {
                      const url = PROJECT_INVOICES_URL.replace(
                        ":project_id",
                        project.id,
                      );
                      navigate(url);
                    }
                  }}
                >
                  <div className="flex items-center gap-[10px] w-full text-secondary-foreground">
                    {project.__typename === "Project" ? (
                      <>
                        <div className="p-1 border-1 border-accent-foreground rounded-sm">
                          <ProjectIcon
                            category={project.projectCategory?.type}
                            color={getProjectIconColor(
                              project.projectCategory?.type,
                            )}
                          />
                        </div>
                        <div className="text-sm">
                          {longTextFormatter(
                            project.name,
                            role !== CompanyRole.Client ? 25 : 40,
                          )}
                        </div>
                        {role !== CompanyRole.Client && (
                          <div className="flex items-center justify-center gap-2 ml-auto font-light text-xs">
                            {!!project.isArchived && (
                              <Lock className="stroke-paint" />
                            )}

                            {longTextFormatter(
                              project.creator.office?.company.profile
                                .companyName,
                              20,
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="p-1 border-1 border-accent-foreground rounded-sm">
                          <FolderOpenDot color="#fcb900" />
                        </div>

                        <div className="text-sm">
                          {longTextFormatter(
                            (project as VendorProjectFragment).project.name,
                            25,
                          )}
                        </div>

                        <div className="ml-auto font-light text-xs">
                          {longTextFormatter(
                            (
                              (project as VendorProjectFragment).company
                                .profile as VendorProfile
                            ).companyName,
                            20,
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </DropdownMenuItem>
              ))}
            </div>
          </InfiniteScroll>
        ) : (
          <div className="text-center font-light text-secondary-foreground py-2">
            No projects found
          </div>
        )}
      </DropdownMenuGroup>
    </>
  );
}

function ProjectSelector() {
  const { currentProject, currentVendorProject } = useUserContext();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="border-accent-foreground border-[1px] rounded-sm">
          <div className="flex items-center gap-2">
            {currentProject && (
              <ProjectIcon category={currentProject.projectCategory?.type} />
            )}
            <div>
              {currentVendorProject?.project.name ||
                currentProject?.name ||
                "All Projects"}
            </div>
          </div>
          <ChevronDown />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="bottom"
        align="start"
        className="py-2 px-3 w-[400px]"
      >
        <DropdownContent />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ProjectSelector;
